import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { GraduationCap } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      nav(`/${u.role}`);
      toast.success(`${t("welcome")}, ${u.name}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <Link to="/" className="mb-6 flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-2xl bg-primary text-primary-foreground">
            <GraduationCap className="h-5 w-5" />
          </div>
          <span className="font-display text-xl font-bold">
            {t("appName")}
          </span>
        </Link>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">
          {t("register")}
        </h1>
        <p className="mt-2 text-muted-foreground">
          {t("tagline")}
        </p>
        <Card className="mt-6 rounded-2xl border-border/70 shadow-sm">
          <CardContent className="p-6">
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t("fullName")}</Label>
                <Input
                  data-testid="reg-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className="h-12"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("email")}</Label>
                  <Input
                    data-testid="reg-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("phone")}</Label>
                  <Input
                    data-testid="reg-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    className="h-12"
                    placeholder="+60"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("password")}</Label>
                <Input
                  data-testid="reg-password"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={set("password")}
                  className="h-12"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="reg-submit"
                className="h-12 w-full rounded-full text-base"
              >
                {busy ? "…" : t("register")}
              </Button>
            </form>
            <div className="mt-4 text-center text-sm text-muted-foreground">
              <Link
                to="/login"
                className="font-semibold text-primary hover:underline"
              >
                {t("login")}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
